import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { sendChatMessage } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const SUGGESTED_PROMPTS_BY_TYPE = {
  guest: [
    'Hvad er Vibra?',
    'Hvor meget koster det?',
    'Hvordan opretter jeg en konto?',
    'Hvilke fag kan jeg finde?',
  ],
  user: [
    'Hvordan booker jeg en håndværker?',
    'Hvad koster abonnementerne?',
    'Hvordan ændrer jeg min adresse?',
    'Hvordan bruger jeg kortet?',
  ],
  company: [
    'Hvordan optimerer jeg min profil?',
    'Hvad er forskellen på Basis, Pro og Premium?',
    'Hvordan besvarer jeg en tilbudsanmodning?',
    'Hvordan får jeg det verificerede badge?',
  ],
};

const Chatbot = () => {
  const { user } = useAuth();
  const userType = user?.user_type || 'guest';
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hej! Jeg er Vibras AI-assistent. Jeg kan hjælpe med at finde rundt på siden, forklare abonnementer, guide dig gennem booking og meget mere. Hvad kan jeg hjælpe med?' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const messagesEndRef = useRef(null);
  const suggestedPrompts = SUGGESTED_PROMPTS_BY_TYPE[userType] || SUGGESTED_PROMPTS_BY_TYPE.guest;
  const showSuggestions = messages.length === 1 && !loading;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = text.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);

    try {
      const res = await sendChatMessage(userMsg, sessionId, userType);
      if (!sessionId) setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Beklager, der opstod en fejl. Prøv igen om et øjeblik.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = () => sendMessage(input);

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-primary text-white rounded-full shadow-lg flex items-center justify-center hover:bg-primary/90 transition-all hover:scale-105"
        data-testid="chatbot-toggle"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {/* Chat Window */}
      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-[380px] max-h-[600px] bg-white rounded-2xl shadow-2xl border flex flex-col overflow-hidden" data-testid="chatbot-window">
          {/* Header */}
          <div className="bg-primary text-white px-4 py-3 flex items-center gap-2">
            <Bot className="w-5 h-5" />
            <div>
              <p className="font-semibold text-sm">Vibra Assistent</p>
              <p className="text-xs text-white/70">Spørg om alt fra siden, priser og booking</p>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[420px]">
            {messages.map((msg, i) => (
              <div key={`${msg.role}-${i}-${(msg.content || '').slice(0,20)}`} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'assistant' && (
                  <div className="w-7 h-7 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-primary" />
                  </div>
                )}
                <div className={`max-w-[78%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                  msg.role === 'user'
                    ? 'bg-primary text-white rounded-br-md'
                    : 'bg-stone-100 text-foreground rounded-bl-md'
                }`}>
                  {msg.content}
                </div>
                {msg.role === 'user' && (
                  <div className="w-7 h-7 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex gap-2 justify-start">
                <div className="w-7 h-7 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-primary" />
                </div>
                <div className="bg-stone-100 px-4 py-2 rounded-2xl rounded-bl-md">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-stone-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            {showSuggestions && (
              <div className="pt-1" data-testid="chatbot-suggestions">
                <div className="flex items-center gap-1.5 mb-2 text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                  <Sparkles className="w-3 h-3" /> Forslag
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {suggestedPrompts.map((prompt) => (
                    <button
                      key={prompt}
                      type="button"
                      onClick={() => sendMessage(prompt)}
                      className="text-xs bg-white border border-stone-200 hover:border-primary hover:bg-primary/5 text-foreground px-3 py-1.5 rounded-full transition-colors"
                      data-testid={`chatbot-suggestion-${prompt.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t">
            <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Skriv en besked..."
                className="flex-1 text-sm"
                disabled={loading}
                data-testid="chatbot-input"
              />
              <Button
                type="submit"
                size="icon"
                disabled={loading || !input.trim()}
                className="rounded-full w-9 h-9"
                data-testid="chatbot-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;
